import { useState } from "react";
import { toast } from "sonner";
import {
  Send, Trash2, Copy, ExternalLink, MessageCircle, FileUp, X,
  Paperclip, Sparkles, Loader2, FileText,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { downloadPdf } from "../../lib/pdf";
import PropertyImage from "../PropertyImage";
import { Field } from "./atoms";
import { STATUS_COLORS, STATUS_LABELS } from "./constants";
import AiDraftDialog from "./AiDraftDialog";
import { fmtUSDateTime } from "../../lib/date";

export default function DealDetailDrawer({ detail, onClose, onChanged }) {
  const { deal, invitations, comments } = detail;
  const [newInv, setNewInv] = useState({ email: "", name: "" });
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);  // { subject, body, model, tone, angle } | null
  const [draftBusy, setDraftBusy] = useState(false);

  const baseUrl = window.location.origin;

  const draftEmail = async (overrides = {}) => {
    setDraftBusy(true);
    try {
      const payload = {
        tone: overrides.tone ?? emailDraft?.tone ?? "professional",
        angle: overrides.angle ?? emailDraft?.angle ?? null,
      };
      const { data } = await api.post(`/deals/${deal.id}/draft-email`, payload);
      setEmailDraft({ ...data, tone: payload.tone, angle: payload.angle });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't draft email");
    } finally {
      setDraftBusy(false);
    }
  };

  const addInvitation = async () => {
    if (!newInv.email?.trim()) return;
    try {
      await api.post(`/deals/${deal.id}/invitations`, {
        invitations: [{ email: newInv.email.trim(), name: newInv.name.trim() || null }],
      });
      setNewInv({ email: "", name: "" });
      toast.success("Investor added");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to invite");
    }
  };

  const removeInvitation = async (inv) => {
    if (!window.confirm(`Remove ${inv.email}?`)) return;
    try {
      await api.delete(`/deals/${deal.id}/invitations/${inv.id}`);
      toast.success("Removed");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  const sendAll = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/deals/${deal.id}/send`);
      toast.success(`${data.sent} invitation${data.sent === 1 ? "" : "s"} sent`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const postReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/deals/${deal.id}/comments`, { body: reply.trim() });
      setReply("");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to post");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/deals/${deal.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attached");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = async (aid) => {
    try {
      await api.delete(`/deals/${deal.id}/attachments/${aid}`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${baseUrl}/deal/view/${token}`);
    toast.success("Investor link copied");
  };

  const pendingCount = (invitations || []).filter(i => !i.sent_at).length;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="deal-detail-drawer">
      <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white overflow-y-auto shadow-2xl border-l border-slate-200">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Deal</div>
            <div className="text-base font-semibold text-slate-900">{deal.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={async () => {
                try {
                  const safe = (deal.name || "deal").replace(/[^A-Za-z0-9 _-]/g, "");
                  await downloadPdf(`/api/deals/${deal.id}/pdf`, `${safe} - Tear Sheet.pdf`);
                  toast.success("Tear sheet downloaded");
                } catch (e) {
                  toast.error("PDF download failed");
                }
              }}
              data-testid="deal-pdf-download-btn"
              title="Download investor tear sheet (PDF)"
              className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-sm">
              <FileText className="w-3.5 h-3.5" /> Tear Sheet PDF
            </button>
            <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-500" data-testid="deal-drawer-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Image + summary */}
          <div>
            <PropertyImage address={deal.formatted_address || deal.address} pid={null} size="600x240" className="w-full" />
            <div className="mt-3 text-xs text-slate-500">{deal.formatted_address || deal.address}</div>
            {deal.executive_summary && (
              <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{deal.executive_summary}</p>
            )}
          </div>

          {/* Attachments */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500">Attachments</h4>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 cursor-pointer inline-flex items-center gap-1.5">
                <FileUp className="w-3.5 h-3.5" />
                {uploading ? "Uploading…" : "Add file"}
                <input type="file" className="hidden" onChange={onUpload} disabled={uploading} data-testid="deal-attach-input" />
              </label>
            </div>
            {(deal.attachments || []).length === 0 ? (
              <div className="text-xs text-slate-400">No files attached.</div>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-sm">
                {(deal.attachments || []).map(a => (
                  <li key={a.id} className="px-3 py-2 flex items-center gap-2">
                    <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-sm text-slate-700 truncate flex-1">{a.name}</span>
                    <span className="text-[10px] font-mono text-slate-400">{(a.size / 1024).toFixed(0)} KB</span>
                    <button onClick={()=>removeAttachment(a.id)} className="text-rose-500 hover:text-rose-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Investors */}
          <section>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500">Investors ({invitations.length})</h4>
              <div className="flex items-center gap-2">
                <button onClick={() => draftEmail()} disabled={draftBusy}
                  data-testid="deal-ai-draft-btn"
                  title="Generate a personalized investor pitch using AI"
                  className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 px-3 py-1.5 rounded-sm disabled:opacity-50">
                  {draftBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {draftBusy ? "Drafting…" : "AI Draft"}
                </button>
                <button onClick={sendAll} disabled={sending || pendingCount === 0}
                  data-testid="deal-send-all-btn"
                  className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-sm">
                  <Send className="w-3.5 h-3.5" />
                  {sending ? "Sending…" : pendingCount === 0 ? "All sent" : `Email ${pendingCount}`}
                </button>
              </div>
            </div>
            <div className="flex items-end gap-2 mb-3">
              <Field label="Email">
                <input className="input-clean w-full text-sm" value={newInv.email}
                  onChange={(e)=>setNewInv({...newInv, email:e.target.value})}
                  placeholder="investor@example.com" data-testid="deal-new-investor-email" />
              </Field>
              <Field label="Name (optional)">
                <input className="input-clean w-full text-sm" value={newInv.name}
                  onChange={(e)=>setNewInv({...newInv, name:e.target.value})}
                  placeholder="Pat Investor" data-testid="deal-new-investor-name" />
              </Field>
              <button onClick={addInvitation}
                className="bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-widest"
                data-testid="deal-add-investor-btn">
                Add
              </button>
            </div>
            {invitations.length === 0 ? (
              <div className="text-xs text-slate-400">No investors invited yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-sm">
                {invitations.map(inv => (
                  <li key={inv.id} className="px-3 py-2.5 flex items-center gap-3 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-800 truncate">{inv.name || inv.email}</div>
                      <div className="text-xs text-slate-500 truncate">{inv.email}</div>
                    </div>
                    {inv.response_status ? (
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_COLORS[inv.response_status] || ""}`}>
                        {STATUS_LABELS[inv.response_status]}
                        {inv.response_amount ? ` · $${Number(inv.response_amount).toLocaleString()}` : ""}
                      </span>
                    ) : inv.sent_at ? (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border bg-slate-50 text-slate-500 border-slate-200">
                        {inv.view_count > 0 ? `Viewed · ${inv.view_count}` : "Sent"}
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                        Draft
                      </span>
                    )}
                    <button onClick={()=>copyLink(inv.token)} title="Copy link" className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-500">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <a href={`${baseUrl}/deal/view/${inv.token}`} target="_blank" rel="noreferrer"
                      title="Preview as investor" className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-500">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button onClick={()=>removeInvitation(inv)} title="Remove"
                      className="p-1.5 rounded-sm hover:bg-rose-50 text-rose-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Comments */}
          <section>
            <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
              <MessageCircle className="w-3.5 h-3.5" /> Q&amp;A
            </h4>
            {comments.length === 0 ? (
              <div className="text-xs text-slate-400 mb-3">No questions yet.</div>
            ) : (
              <ul className="space-y-3 mb-3">
                {comments.map(c => (
                  <li key={c.id} className={`rounded-sm border p-3 text-sm ${c.author_role === "sponsor" ? "bg-slate-50 border-slate-200" : "bg-amber-50/40 border-amber-100"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-mono uppercase tracking-widest text-slate-500">
                        {c.author_role === "sponsor" ? "You" : c.author_name || c.author_email}
                      </div>
                      <div className="text-[10px] font-mono text-slate-400">{fmtUSDateTime(c.created_at)}</div>
                    </div>
                    <div className="text-slate-700 whitespace-pre-wrap">{c.body}</div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-end gap-2">
              <textarea rows={2} className="input-clean w-full" placeholder="Reply to your investors…"
                value={reply} onChange={(e)=>setReply(e.target.value)} data-testid="deal-reply-input" />
              <button onClick={postReply} disabled={!reply.trim()}
                className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-xs font-mono uppercase tracking-widest"
                data-testid="deal-reply-btn">
                Post
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* AI Email Draft Preview Modal */}
      {emailDraft && (
        <AiDraftDialog
          draft={emailDraft}
          deal={deal}
          onClose={()=>setEmailDraft(null)}
          onRegenerate={(overrides)=>draftEmail(overrides)}
          onChange={(patch)=>setEmailDraft({ ...emailDraft, ...patch })}
          onSavedSummary={()=>{ onChanged(); setEmailDraft(null); }}
          busy={draftBusy}
        />
      )}
    </div>
  );
}
